import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileText, X, ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";
import { validateEmail, ERR_INPUT_CLS, ERR_MSG_CLS } from "@/lib/formValidation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hcg_checklist_optin_state_v1";

/**
 * Email-capture card for high-traffic editorial guides.
 *
 * Renders two coordinated UI pieces:
 *   1. <ChecklistOptin /> inline card at the bottom of the article (always rendered)
 *   2. A sticky bottom-of-viewport bar that appears once the user has scrolled
 *      past ~30% of the page. The bar self-hides when the user is near the
 *      inline card (so they're not double-prompted), or after submit/dismiss.
 *
 * Single email input → split prefix to satisfy backend's required `name` field.
 * Posts to /api/leads with `source` + `notes` carrying the offer identifier.
 *
 * Why a sticky bar? Long-form guides convert email opt-ins ~3× higher when
 * a non-modal CTA persists during scroll vs. only an inline form at the end.
 */
export default function ChecklistOptin({
  title = "Get the 6-line pre-signing checklist",
  description = "We'll email you the printable checklist plus the 8 over-budget categories you can flag to any contractor before signing.",
  benefit = "One email. Unsubscribe in one click. No spam.",
  source = "checklist-optin",
  notes = "Requested: Pre-signing renovation checklist",
  stickyLabel = "Get the printable pre-signing checklist",
}) {
  const [submitted, setSubmitted] = useState(false);
  const [dismissedSticky, setDismissedSticky] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const inlineRef = useRef(null);

  // Persist submit/dismiss for the rest of the session so we don't re-prompt.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.submitted) setSubmitted(true);
        if (s.dismissed) setDismissedSticky(true);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    function onScroll() {
      const doc = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / doc.scrollHeight;
      const past30 = scrolled > 0.3;
      // Hide sticky bar when the inline card is near/in view to avoid double-prompt.
      let inlineInView = false;
      if (inlineRef.current) {
        const rect = inlineRef.current.getBoundingClientRect();
        inlineInView = rect.top < window.innerHeight - 100;
      }
      setShowSticky(past30 && !inlineInView);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const persist = (next) => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const handleSubmit = async (email) => {
    const trimmed = (email || "").trim();
    if (!trimmed) return;
    const derivedName = trimmed.split("@")[0].replace(/[._-]+/g, " ").slice(0, 60) || "Reader";
    try {
      await axios.post(`${API}/leads`, {
        name: derivedName,
        email: trimmed,
        source,
        notes,
      });
      track("checklist_optin_submit", { source });
      setSubmitted(true);
      persist({ submitted: true });
      toast.success("Sent! Check your inbox for the checklist.");
    } catch {
      toast.error("Couldn't sign you up — try again in a moment?");
    }
  };

  const handleDismissSticky = () => {
    setDismissedSticky(true);
    persist({ dismissed: true });
    track("checklist_optin_dismiss_sticky", { source });
  };

  return (
    <>
      <div ref={inlineRef}>
        <InlineCard
          title={title}
          description={description}
          benefit={benefit}
          submitted={submitted}
          onSubmit={handleSubmit}
        />
      </div>

      {!submitted && !dismissedSticky && showSticky && (
        <StickyBar
          label={stickyLabel}
          onSubmit={handleSubmit}
          onDismiss={handleDismissSticky}
        />
      )}
    </>
  );
}

function InlineCard({ title, description, benefit, submitted, onSubmit }) {
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (submitted) {
    return (
      <div
        className="mt-14 overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50/70 p-6 sm:p-8"
        data-testid="checklist-optin-success"
      >
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
            <CheckCircle2 className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-xl font-semibold text-emerald-900">
              Sent — check your inbox.
            </h3>
            <p className="mt-1 text-sm text-emerald-900/80">
              We just emailed you the pre-signing checklist. While you're waiting, run a quick state-adjusted
              cost estimate on any of our calculators below.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-14 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 sm:p-8"
      data-testid="checklist-optin-card"
    >
      <div className="flex items-start gap-4 sm:gap-5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-white">
          <FileText className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-display text-xl font-semibold text-slate-900 sm:text-2xl">
            {title}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
            {description}
          </p>
          <form
            className="mt-5 flex flex-col gap-2 sm:flex-row"
            noValidate
            onSubmit={async (e) => {
              e.preventDefault();
              const err = validateEmail(email, {
                requiredMsg: "Enter your email so we can send the checklist.",
              });
              if (err) {
                setEmailErr(err);
                track("optin_submit_attempted_with_errors", { form: "checklist-optin-inline", reason: err });
                return;
              }
              setEmailErr("");
              setBusy(true);
              try {
                await onSubmit(email);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (emailErr) setEmailErr(""); }}
              placeholder="your@email.com"
              aria-invalid={!!emailErr}
              className={`h-11 flex-1 rounded-full bg-white px-5 text-base ${emailErr ? ERR_INPUT_CLS : "border-slate-300"}`}
              data-testid="checklist-optin-email"
            />
            <Button
              type="submit"
              disabled={busy}
              className="group h-11 rounded-full bg-green-600 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:opacity-60"
              data-testid="checklist-optin-submit"
            >
              {busy ? "Sending…" : "Email me the checklist"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>
          {emailErr && (
            <p className={`mt-2 ${ERR_MSG_CLS}`} data-testid="checklist-optin-email-error">{emailErr}</p>
          )}
          <p className="mt-3 text-xs text-slate-500">{benefit}</p>
        </div>
      </div>
    </div>
  );
}

function StickyBar({ label, onSubmit, onDismiss }) {
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const err = validateEmail(email, {
      requiredMsg: "Enter your email to get the checklist.",
    });
    if (err) {
      setEmailErr(err);
      track("optin_submit_attempted_with_errors", { form: "checklist-optin-sticky", reason: err });
      return;
    }
    setEmailErr("");
    setBusy(true);
    try {
      await onSubmit(email);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-3 sm:pb-5"
      data-testid="checklist-optin-sticky"
    >
      <div className="pointer-events-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-xl backdrop-blur supports-[backdrop-filter]:bg-white/80 sm:p-4">
        <div className="flex items-center gap-3">
          <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-white sm:flex">
            <FileText className="h-4 w-4" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-semibold leading-tight text-slate-900 sm:text-[15px]">
              {label}
            </p>
            <p className="hidden text-xs text-slate-500 sm:block">
              Free. One email. Unsubscribe in one click.
            </p>
          </div>
          <form
            className="flex items-center gap-2"
            noValidate
            onSubmit={submit}
          >
            <Input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (emailErr) setEmailErr(""); }}
              placeholder="your@email.com"
              aria-invalid={!!emailErr}
              className={`hidden h-10 w-56 rounded-full bg-white px-4 text-sm md:block ${emailErr ? ERR_INPUT_CLS : "border-slate-300"}`}
              data-testid="checklist-optin-sticky-email"
            />
            <Button
              type="submit"
              disabled={busy}
              className="h-10 rounded-full bg-green-600 px-4 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
              data-testid="checklist-optin-sticky-submit"
            >
              {busy ? "Sending…" : (
                <>
                  <span className="hidden md:inline">Email it to me</span>
                  <span className="md:hidden">Email it →</span>
                </>
              )}
            </Button>
          </form>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="ml-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            data-testid="checklist-optin-sticky-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* Mobile: stack email input below since the row is tight */}
        <form
          className="mt-2 flex items-center gap-2 md:hidden"
          noValidate
          onSubmit={submit}
        >
          <Input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (emailErr) setEmailErr(""); }}
            placeholder="your@email.com"
            aria-invalid={!!emailErr}
            className={`h-10 flex-1 rounded-full bg-white px-4 text-sm ${emailErr ? ERR_INPUT_CLS : "border-slate-300"}`}
            data-testid="checklist-optin-sticky-email-mobile"
          />
        </form>
        {emailErr && (
          <p className={`mt-2 px-1 ${ERR_MSG_CLS}`} data-testid="checklist-optin-sticky-email-error">{emailErr}</p>
        )}
      </div>
    </div>
  );
}
